<div class="scorecard-preview-dialog">

  <!-- ═══ STEP 1: Pick File ═══ -->
  <ng-container *ngIf="step === 'pick-file'">
    <h2 mat-dialog-title>Upload Scorecard</h2>
    <mat-dialog-content>
      <div class="drop-zone" (drop)="onDrop($event)" (dragover)="onDragOver($event)" (click)="onPickFile()">
        <mat-icon class="upload-icon">cloud_upload</mat-icon>
        <p class="drop-text">Drag & drop a scorecard image here</p>
        <p class="drop-subtext">or click to browse</p>
        <p class="drop-formats">JPG, PNG, or AVIF · Max 10 MB</p>
      </div>
      <input type="file" #fileInput hidden accept="image/jpeg,image/png,image/avif" (change)="onFileSelected($event)">
      <div class="file-error" *ngIf="fileError">
        <mat-icon>error_outline</mat-icon>
        <span>{{ fileError }}</span>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
    </mat-dialog-actions>
  </ng-container>

  <!-- ═══ STEP 2: Processing ═══ -->
  <ng-container *ngIf="step === 'processing'">
    <h2 mat-dialog-title>Analyzing Scorecard</h2>
    <mat-dialog-content>
      <div class="processing-section">
        <p class="file-name">{{ selectedFileName }}</p>
        <div class="progress-list">
          <div class="progress-item" *ngFor="let item of progressSteps" [ngClass]="item.status">
            <mat-icon *ngIf="item.status === 'done'" class="step-icon done">check_circle</mat-icon>
            <mat-icon *ngIf="item.status === 'active'" class="step-icon active">hourglass_top</mat-icon>
            <mat-icon *ngIf="item.status === 'pending'" class="step-icon pending">radio_button_unchecked</mat-icon>
            <mat-icon *ngIf="item.status === 'error'" class="step-icon error">error</mat-icon>
            <span class="step-label">{{ item.label }}</span>
          </div>
        </div>
        <p class="elapsed-time" *ngIf="elapsedSeconds > 3 && !errorMessage">
          <span class="status-text" *ngIf="statusMessage">{{ statusMessage }}</span>
          <span class="elapsed-counter">{{ elapsedSeconds }}s elapsed</span>
          <span *ngIf="elapsedSeconds > 30" class="elapsed-note"> — complex scorecards may take a few minutes</span>
        </p>
        <div class="error-message" *ngIf="errorMessage">
          <mat-icon>error</mat-icon>
          <span>{{ errorMessage }}</span>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="primary" *ngIf="errorMessage" (click)="onRetry()">Try Again</button>
    </mat-dialog-actions>
  </ng-container>

  <!-- ═══ Wrong Course Detected ═══ -->
  <ng-container *ngIf="step === 'wrong-course'">
    <h2 mat-dialog-title>Course Name Mismatch</h2>
    <mat-dialog-content>
      <div class="missing-rating-section">
        <mat-icon class="missing-icon" style="color: #f57c00;">warning</mat-icon>
        <p>This scorecard appears to be for:</p>
        <p style="font-size: 18px; font-weight: 600; margin: 8px 0;">{{ detectedCourseName }}</p>
        <p>But you're updating:</p>
        <p style="font-size: 18px; font-weight: 600; margin: 8px 0;">{{ expectedCourseName }}</p>
        <p class="missing-subtext">If this is correct (e.g., the course changed names), you can continue anyway.</p>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="onContinueWrongCourse()">Continue Anyway</button>
    </mat-dialog-actions>
  </ng-container>

  <!-- ═══ STEP 2.5: Missing Rating/Slope ═══ -->
  <ng-container *ngIf="step === 'missing-rating'">
    <h2 mat-dialog-title>Missing Slope / Rating</h2>
    <mat-dialog-content>
      <div class="missing-rating-section">
        <mat-icon class="missing-icon">help_outline</mat-icon>
        <p>We couldn't find slope and rating data for the following tees:</p>
        <ul>
          <li *ngFor="let tee of missingRatingTees"><strong>{{ tee }}</strong></li>
        </ul>
        <p class="missing-subtext">
          Some scorecards display slope and rating on a separate section or card.
          Do you have another image with this information?
        </p>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button (click)="onContinueWithoutRating()">Continue Without</button>
      <!-- Future: <button mat-raised-button color="primary" (click)="onUploadRatingCard()">Upload Rating Card</button> -->
    </mat-dialog-actions>
  </ng-container>

  <!-- ═══ STEP 3: Review ═══ -->
  <ng-container *ngIf="step === 'review'">
    <h2 mat-dialog-title>Does this look right?</h2>
    <mat-dialog-content>

      <!-- Warnings Section -->
      <div class="warnings-section" *ngIf="warnings.length > 0">
        <div class="warning-banner">
          <mat-icon>warning</mat-icon>
          <div class="warning-content">
            <strong>Active Event Warning</strong>
            <p>These tees are used by active events and won't be modified. New records will be created instead.</p>
            <ul>
              <li *ngFor="let warning of warnings">
                <strong>{{ warning.event_name }}</strong> ({{ warning.event_date }}) — Tee ID: {{ warning.tees_id }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Skipped Tees Notice -->
      <div class="skipped-section" *ngIf="skippedTees.length > 0">
        <div class="skipped-banner">
          <mat-icon>info</mat-icon>
          <div class="skipped-content">
            <strong>These tees couldn't be fully extracted:</strong>
            <ul>
              <li *ngFor="let tee of skippedTees">{{ tee }}</li>
            </ul>
            <p>Their yardage, rating, and slope won't be changed. Would you like to copy just the par and handicap values from a successful tee?</p>
            <button mat-raised-button color="primary" (click)="copyHdcpParToSkipped()" *ngIf="teeChanges.length > 0"
              style="margin-top: 8px;">
              Copy Par & Handicap from "{{ teeChanges[0].tee_name }}" to these tees
            </button>
          </div>
        </div>
      </div>

      <!-- Tee Changes -->
      <div class="tee-changes-section">
        <div class="tee-change" *ngFor="let change of teeChanges; let i = index" [ngClass]="{'skipped': change.skipped}">
          <div class="tee-change-header">
            <span class="tee-name">{{ change.tee_name }}</span>
            <span class="change-label" [ngClass]="{'label-new': change.type === 'insert', 'label-update': change.type === 'update'}">
              {{ getChangeLabel(change) }}
            </span>
            <span style="flex: 1;"></span>
            <button mat-stroked-button color="warn" (click)="skipTee(i)" *ngIf="!change.skipped" style="font-size: 11px; line-height: 24px; padding: 0 10px;">
              Skip
            </button>
            <button mat-stroked-button (click)="unskipTee(i)" *ngIf="change.skipped" style="font-size: 11px; line-height: 24px; padding: 0 10px;">
              Include
            </button>
          </div>

          <!-- Summary Table -->
          <table class="summary-table">
            <thead>
              <tr>
                <th></th>
                <th>Tee Name</th>
                <th>Rating</th>
                <th>Slope</th>
                <th>Par</th>
                <th>Total Yards</th>
              </tr>
            </thead>
            <tbody>
              <tr class="before-row" *ngIf="change.type === 'update'">
                <td class="row-label">Before</td>
                <td>{{ change.before?.teename }}</td>
                <td>{{ change.before?.rating }}</td>
                <td>{{ change.before?.slope }}</td>
                <td>{{ change.before?.par }}</td>
                <td>{{ change.before?.total_yards }}</td>
              </tr>
              <tr class="before-row empty" *ngIf="change.type === 'insert'">
                <td class="row-label">Before</td>
                <td colspan="5" class="empty-cell">—</td>
              </tr>
              <tr class="after-row">
                <td class="row-label">After</td>
                <td>{{ change.after.teename }}</td>
                <td>{{ change.after.rating }}</td>
                <td>{{ change.after.slope }}</td>
                <td>{{ change.after.par }}</td>
                <td>{{ change.after.total_yards }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Hole-by-Hole Data -->
          <div class="hole-data-section">
            <table class="hole-table">
              <thead>
                <tr>
                  <th>Hole</th>
                  <th *ngFor="let hole of holeNumbers">{{ hole }}</th>
                </tr>
              </thead>
              <tbody>
                <tr class="before-row" *ngIf="change.type === 'update'">
                  <td class="row-label">Yards (Before)</td>
                  <td *ngFor="let hole of change.before?.holes">{{ hole.yards }}</td>
                </tr>
                <tr class="after-row">
                  <td class="row-label">Yards</td>
                  <td *ngFor="let hole of change.after.holes">{{ hole.yards }}</td>
                </tr>
                <tr class="before-row" *ngIf="change.type === 'update'">
                  <td class="row-label">Par (Before)</td>
                  <td *ngFor="let hole of change.before?.holes">{{ hole.par }}</td>
                </tr>
                <tr class="after-row">
                  <td class="row-label">Par</td>
                  <td *ngFor="let hole of change.after.holes">{{ hole.par }}</td>
                </tr>
                <tr class="before-row" *ngIf="change.type === 'update'">
                  <td class="row-label">Hdcp (Before)</td>
                  <td *ngFor="let hole of change.before?.holes">{{ hole.hdcp }}</td>
                </tr>
                <tr class="after-row">
                  <td class="row-label">Hdcp</td>
                  <td *ngFor="let hole of change.after.holes">{{ hole.hdcp }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div class="error-message" *ngIf="errorMessage">
        <mat-icon>error</mat-icon>
        <span>{{ errorMessage }}</span>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-button (click)="onRetry()">Re-upload</button>
      <button mat-raised-button color="primary" (click)="onApply()" [disabled]="applying">
        <span>Apply Changes</span>
      </button>
    </mat-dialog-actions>
  </ng-container>

  <!-- ═══ STEP 4: Applying ═══ -->
  <ng-container *ngIf="step === 'applying'">
    <h2 mat-dialog-title>Applying Changes</h2>
    <mat-dialog-content>
      <div class="applying-section">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Writing scorecard data to database...</p>
      </div>
    </mat-dialog-content>
  </ng-container>

  <!-- ═══ STEP 5: Rating ═══ -->
  <ng-container *ngIf="step === 'rating'">
    <h2 mat-dialog-title>{{ applyResult ? 'Changes Applied!' : 'Thanks for trying!' }}</h2>
    <mat-dialog-content>
      <div class="rating-section">
        <p>How accurate was this extraction?</p>
        <div class="star-rating">
          <mat-icon *ngFor="let star of [1,2,3,4,5]"
            (click)="setRating(star)"
            [ngClass]="{'filled': userRating && star <= userRating}">
            {{ userRating && star <= userRating ? 'star' : 'star_border' }}
          </mat-icon>
        </div>
        <mat-form-field appearance="outline" style="width: 100%; margin-top: 12px;">
          <mat-label>Any notes? (optional)</mat-label>
          <textarea matInput [(ngModel)]="userNotes" rows="2"></textarea>
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="skipRating()">Skip</button>
      <button mat-raised-button color="primary" (click)="submitRating()">Done</button>
    </mat-dialog-actions>
  </ng-container>

</div>
